<script lang="ts">
  import { map, uniq, pick } from 'lodash-es'
  import { isNonEmptyArray } from '#app/lib/boolean_tests'
  import ItemShowModal from '#inventory/components/item_show_modal.svelte'
  import type { SerializedItemWithUserData } from '#inventory/lib/items'
  import { transactionsData } from '#inventory/lib/transactions_data'
  import ItemMarker from '#map/components/item_marker.svelte'
  import LeafletMap from '#map/components/leaflet_map.svelte'
  import MapFilters from '#map/components/map_filters.svelte'
  import Marker from '#map/components/marker.svelte'
  import UserMarker from '#map/components/user_marker.svelte'
  import type { EntityUri } from '#server/types/entity'
  import { i18n } from '#user/lib/i18n'
  import { mainUserStore } from '#user/lib/serialized_main_user'
  import { getDocsBounds } from './lib/map.ts'

  let {
    itemsToDisplay = $bindable([]),
    filteredItems,
  }: {
    itemsToDisplay?: SerializedItemWithUserData[]
    filteredItems?: SerializedItemWithUserData[]
  } = $props()

  const bounds = $derived(itemsToDisplay ? getDocsBounds(itemsToDisplay) : null)
  let allEditionsFilters = $derived(isNonEmptyArray(itemsToDisplay) ? uniq(map(itemsToDisplay, 'entity')) : [])
  let allTransactionFilters = $derived(isNonEmptyArray(itemsToDisplay) ? uniq(map(itemsToDisplay, 'transaction')) : [])
  const editionsFiltersData = $derived(isNonEmptyArray(itemsToDisplay) ? itemsToDisplay.reduce(aggregateItemsByEntityUri, {}) : null)
  const transactionFiltersData = $derived(pick(transactionsData, allTransactionFilters))

  let selectedTransactionFilters = $state([])
  let selectedEditionFilters = $state([])
  $effect(() => {
    if (itemsToDisplay) {
      selectedEditionFilters = allEditionsFilters
      selectedTransactionFilters = allTransactionFilters
    }
  })

  const resetDocsToDisplay = () => itemsToDisplay = filteredItems

  function aggregateItemsByEntityUri (filtersData: Record<EntityUri, SerializedItemWithUserData>, item: SerializedItemWithUserData) {
    return { ...filtersData, [item.entity]: item }
  }

  function isFilterSelected (item: SerializedItemWithUserData) {
    if (!item.position) return false
    if (!selectedEditionFilters.includes(item.entity)) return false
    if (!selectedTransactionFilters.includes(item.transaction)) return false
    return true
  }

  const displayedItems: SerializedItemWithUserData[] = $derived.by(() => {
    return itemsToDisplay.filter(isFilterSelected)
  })

  function findMainUserItems (displayedItems: SerializedItemWithUserData[]) {
    return displayedItems.find(item => item.owner === $mainUserStore?._id) != null
  }

  const notAllDocsAreDisplayed = $derived(displayedItems.length !== filteredItems?.length)
  const someMainUserItemIsDisplayed = $derived(findMainUserItems(displayedItems))
  let modalItem: SerializedItemWithUserData | undefined = $state()
</script>

<div class="items-map">
  {#if bounds.length > 0}
    <LeafletMap
      bounds={$mainUserStore?.position ? bounds.concat([ $mainUserStore.position ]) : bounds}
      cluster={true}
    >
      {#each displayedItems as item (item._id)}
        <Marker latLng={item.position}>
          <ItemMarker {item} on:showItem={() => modalItem = item} />
        </Marker>
      {/each}
      {#if $mainUserStore?.position && !someMainUserItemIsDisplayed}
        <Marker latLng={$mainUserStore.position} standalone={true}>
          <UserMarker doc={$mainUserStore} />
        </Marker>
      {/if}
    </LeafletMap>
  {/if}
</div>
{#if allTransactionFilters?.length > 1}
  <MapFilters
    type="transaction"
    bind:selectedFilters={selectedTransactionFilters}
    filtersData={transactionFiltersData}
    bind:allFilters={allTransactionFilters}
    translatableFilterValues={true}
  />
{/if}
{#if allEditionsFilters?.length > 1}
  <MapFilters
    type="editions"
    bind:selectedFilters={selectedEditionFilters}
    filtersData={editionsFiltersData}
    bind:allFilters={allEditionsFilters}
  />
{/if}
{#if filteredItems && notAllDocsAreDisplayed}
  <div class="show-all-wrapper">
    <button class="show-all-button" onclick={resetDocsToDisplay}>
      {i18n('Show every books on map')}
    </button>
  </div>
{/if}

{#if modalItem}
  <ItemShowModal
    item={modalItem}
    showItemModal={modalItem != null}
    onClosingModal={() => modalItem = null}
  />
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .items-map{
    position: relative;
    height: 30em;
    width: 100%;
  }
  .show-all-wrapper{
    @include display-flex(column, flex-end);
    margin-block-end: 1em;
  }
  .show-all-button{
    @include tiny-button($light-grey);
    @include text-hover(#333);
    margin: 0.2em;
    padding: 0.5em;
  }
</style>
